/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { alpha, IconButton, Link as MuiLink, Stack, type Theme, Typography } from '@mui/material';
import { type AbsoluteURL, formatDateLocal, type LastSimulationRecord } from '@poweralpha/common';
import { useMemo, useState } from 'react';
import RecordList from '../../react-components/RecordList';
import ScenarioName from '../../react-components/simulation-results/ScenarioName';
import { InfoPill } from '../../react-components/text';
import Widget from '../../react-components/Widget';
import { bodColor, serviceColorMap } from '../../styles';
import { useLastSimulationsQuery } from '../api';

const LAST_SIMULATIONS_PAGE_SIZE = 10;
const LAST_SIMULATIONS_MAX_LIMIT = 25;

const formatPoiSize = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) {
    return null;
  }

  return `POI ${value.toFixed(1)} MW`;
};

const getSimulationLink = (simulation: LastSimulationRecord): AbsoluteURL => {
  switch (simulation.service) {
    case 'PVSim':
      return `/pv/pvsim/results?scenario=${simulation.scenario_id}`;
    case 'PVCosting':
      return `/pv/costing/simulate?scenario=${simulation.scenario_id}`;
    case 'ESS':
      return `/ess/costing/simulate?scenario=${simulation.scenario_id}`;
    case 'PlantSim':
      return `/plant/results?scenario=${simulation.scenario_id}`;
    default:
      return '/projects';
  }
};

const getSimulationItemSx = (simulation: LastSimulationRecord) => (theme: Theme) => {
  if (!simulation.stage) return {};

  const { palette } = theme;
  if (simulation.stage.as_built_design) {
    return {
      backgroundColor: alpha(palette.success.light, palette.mode === 'dark' ? 0.2 : 0.2),
      '&:hover': {
        backgroundColor: alpha(palette.success.light, palette.mode === 'dark' ? 0.3 : 0.3),
      },
    };
  }

  if (simulation.stage.por_design) {
    return {
      backgroundColor: alpha(palette.info.light, 0.2),
      '&:hover': {
        backgroundColor: alpha(palette.info.light, 0.3),
      },
    };
  }

  return {
    backgroundColor: alpha(bodColor, 0.2),
    '&:hover': {
      backgroundColor: alpha(bodColor, 0.3),
    },
  };
};

const getRow = (simulation: LastSimulationRecord) => {
  const poiSizeLabel = formatPoiSize(simulation.poi_mw_ac);

  return (
    <Stack
      spacing={0.5}
      sx={{
        width: '100%',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            alignItems: 'baseline',
            minWidth: 0,
          }}
        >
          <MuiLink
            component="span"
            underline="always"
            sx={{
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.2,
              color: 'primary.main',
            }}
            noWrap
          >
            <ScenarioName
              runName={simulation.run_name}
              stage={simulation.stage}
              hideTimestamp
              iconsOnly
            />
          </MuiLink>
          {simulation.project_name && (
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: 'text.secondary',
                lineHeight: 1.2,
              }}
            >
              {simulation.project_name}
            </Typography>
          )}
        </Stack>
        <InfoPill
          label={simulation.service}
          color={serviceColorMap[simulation.service]}
          height={18}
        />
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: 'text.secondary',
            }}
          >
            {formatDateLocal(simulation.simulation_time)}
          </Typography>
        </Stack>
        {poiSizeLabel && (
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: 'text.secondary',
              textAlign: 'right',
            }}
          >
            {poiSizeLabel}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

function LastSimulations() {
  const [limit, setLimit] = useState(LAST_SIMULATIONS_PAGE_SIZE);
  const lastSimulationsQuery = useLastSimulationsQuery({
    limit,
  });
  const hasMore = useMemo(() => {
    const rowCount = lastSimulationsQuery.data?.length ?? 0;
    return rowCount >= limit && limit < LAST_SIMULATIONS_MAX_LIMIT;
  }, [lastSimulationsQuery.data?.length, limit]);

  return (
    <Widget
      title="My last simulations"
      actions={
        <IconButton
          loading={lastSimulationsQuery.isRefetching}
          size="small"
          onClick={() => lastSimulationsQuery.refetch()}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      }
    >
      <RecordList
        label="simulation"
        queryResult={lastSimulationsQuery}
        getRow={getRow}
        getLink={getSimulationLink}
        customButtonSx={getSimulationItemSx}
        idKey="run_id"
        hasMore={hasMore}
        onLoadMore={() =>
          setLimit((currentLimit) =>
            Math.min(currentLimit + LAST_SIMULATIONS_PAGE_SIZE, LAST_SIMULATIONS_MAX_LIMIT),
          )
        }
        isLoadingMore={lastSimulationsQuery.isFetching && !lastSimulationsQuery.isLoading}
      />
    </Widget>
  );
}

export default LastSimulations;
